import React, { useRef, useEffect, useState, lazy, Suspense } from "react";
import LayOutContainer from "../../layout/LayOutContainer";
import Ticker from "./../Ticker";
// Importar componentes críticos directamente (sin lazy loading) para el LCP
import Hero from "./../Hero";
import BannerPromo from "./../BannerPromo";
import { AdCard } from "@a365/dsys";
import { mountRootParcel } from "single-spa";
// Lazy load componentes no críticos para la primera carga
const Feature1 = lazy(() => import("./../Feature1"));
const Feature2 = lazy(() => import("./../Feature2"));
const Feature3 = lazy(() => import("./../Feature3"));
const Feature4 = lazy(() => import("./../Feature4"));
const Feature5 = lazy(() => import("./../Feature5"));
const BannerFullX = lazy(() => import("./../BannerFullX"));
const BannerPaymentMethods = lazy(() => import("./../BannerPaymentMethods"));

const Faq = lazy(() => import("./../Faq"));
const Newsletter = lazy(() => import("./../Newsletter"));
const BannerPreFooter = lazy(() => import("../BannerPreFooter"));
const BlogFeeds = lazy(() => import("../BlogFeeds"));
const LandingSliderMx = lazy(() => import("../LandingSliderMx"));
const LandingSliderAr = lazy(() => import("../LandingSliderAr"));
const LandingSliderBr = lazy(() => import("../LandingSliderBr"));
const LandingSliderCl = lazy(() => import("../LandingSliderCl"));
// Import all skeleton components
import HeroSkeleton from "../Skeletons/Hero";
import Feature1Skeleton from "../Skeletons/Feature1";
import Feature2Skeleton from "../Skeletons/Feature2";
import Feature3Skeleton from "../Skeletons/Feature3";
import Feature4Skeleton from "../Skeletons/Feature4";
import Feature5Skeleton from "../Skeletons/Feature5";
import BannerPromoSkeleton from "../Skeletons/BannerPromo";
import BannerFullXSkeleton from "../Skeletons/BannerFullX";
import BannerPaymentMethodsSkeleton from "../Skeletons/BannerPaymentMethods";
import FaqSkeleton from "../Skeletons/Faq";
import NewsletterSkeleton from "../Skeletons/Newsletter";
import BannerPreFooterSkeleton from "../Skeletons/BannerPreFooter";
import LandingSliderSkeleton from "../Skeletons/LandingSlider";
import BlogFeedsSkeleton from "../Skeletons/BlogFeeds";

const Home = () => {
  const [openMod, setOpenMod] = useState(false);
  const [currentCountry, setCurrentCountry] = useState("");
  const coveragesRef = useRef(null);
  const [coveragesParcelMounted, setCoveragesParcelMounted] = useState(false);
  const mountedCoveragesParcelRef = useRef(null);
  const geo = location.pathname.split("/")[1];
  const sliderComponents = {
    ar: <LandingSliderAr />,
    mx: <LandingSliderMx />,
    br: <LandingSliderBr />,
    cl: <LandingSliderCl />,
  };

  // Efecto para establecer el país actual basado en geo
  useEffect(() => {
    if (geo && ["ar", "mx", "br", "cl"].includes(geo)) {
      setCurrentCountry(geo);
    } else {
      setCurrentCountry("ar"); // Default country
    }

    // Agregar mark de timing para LCP
    if (window.performance && window.performance.mark) {
      window.performance.mark("hero-rendered");
    }

    // Verificar si hay parámetros URL para abrir el cotizador
    const urlParams = new URLSearchParams(window.location.search);
    const openQuoter = urlParams.get('openQuoter');
    const source = urlParams.get('source');
    
    if (openQuoter === 'true') {
      console.log('Home: Detected openQuoter parameter from:', source);
      
      // Limpiar los parámetros de la URL sin recargar la página
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Abrir el cotizador después de un pequeño delay para asegurar que todo esté cargado
      setTimeout(() => {
        setOpenMod(true);
        console.log('Home: Quoter modal opened from URL parameter');
      }, 100);
    }
  }, [geo]);

  const scrollToAnchor = (anchorID) => {
    const element = document.getElementById(anchorID);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openCoverages = async () => {
    // Si ya está montado, desmontarlo
    if (coveragesParcelMounted && mountedCoveragesParcelRef.current) {
      try {
        await mountedCoveragesParcelRef.current.unmount();
        setCoveragesParcelMounted(false);
        mountedCoveragesParcelRef.current = null;
      } catch (error) {
        console.error("Error al desmontar la parcela Coverages:", error);
      }
      return;
    }

    // Verificar que la función mountRootParcel esté disponible
    if (!mountRootParcel) {
      console.error("mountRootParcel no está disponible");
      return;
    }

    // Verificar que el contenedor DOM existe
    if (!coveragesRef.current) {
      console.error("El contenedor DOM para coverages no está disponible");
      return;
    }

    try {
      const parcelModule = await window.System.import("@a365/shared-ui");
      const mountedParcel = mountRootParcel(
        parcelModule.default || parcelModule,
        {
          domElement: coveragesRef.current,
          // Props que se pasarán al componente
          show: "coverages",
          autoOpen: true, // Automatically open the dialog when mounted
          // Callback para cuando el dialog se cierre
          onCancel: async (result) => {
            // Desmontar la parcela cuando el modal se cierra
            if (mountedCoveragesParcelRef.current) {
              try {
                await mountedCoveragesParcelRef.current.unmount();
                setCoveragesParcelMounted(false);
                mountedCoveragesParcelRef.current = null;
              } catch (err) {
                console.error("Error al desmontar Coverages en onCancel:", err);
              }
            }
          },
        }
      );

      // Guardar referencia al parcel montado para poder desmontarlo después
      mountedCoveragesParcelRef.current = mountedParcel;
      setCoveragesParcelMounted(true);
    } catch (error) {
      console.error("Error al montar la parcela Coverages:", error);
    }
  };

  // Event listener for cross-microfrontend communication to open quoter
  useEffect(() => {
    const handleOpenQuoter = (event) => {
      try {
        console.log('Home: Received openQuoter event', event.detail);
        
        // Validar que el evento tiene la estructura esperada
        if (event.detail && event.detail.source === 'my-voucher') {
          setOpenMod(true);
          console.log('Home: Quoter modal opened successfully');
        } else {
          console.warn('Home: Invalid openQuoter event structure', event.detail);
        }
      } catch (error) {
        console.error('Home: Error handling openQuoter event', error);
      }
    };

    window.addEventListener('openQuoter', handleOpenQuoter);
    
    return () => {
      window.removeEventListener('openQuoter', handleOpenQuoter);
    };
  }, []);

  // Cleanup effect to restore body styles and remove overlays when component unmounts
  useEffect(() => {
    return () => {
      // Restore body overflow
      document.body.style.overflow = "";
      
      // Remove any quoter overlays that might be left behind
      const quoterOverlay = document.getElementById("quoter--overlay");
      if (quoterOverlay) {
        quoterOverlay.remove();
        console.log('Home: Cleaned up quoter overlay on unmount');
      }

      // Remove any other potential overlays
      const overlays = document.querySelectorAll('[id*="overlay"], [class*="overlay"]');
      overlays.forEach(overlay => {
        if (overlay.id === "quoter--overlay" || overlay.className.includes('bg-black')) {
          overlay.remove();
        }
      });
    };
  }, []);

  // Handle browser navigation events (back/forward) to cleanup state
  useEffect(() => {
    const handlePopState = () => {
      console.log('Home: Browser navigation detected, cleaning up quoter state');
      
      // Close quoter modal if open
      setOpenMod(false);
      
      // Restore body overflow
      document.body.style.overflow = "";
      
      // Remove quoter overlays
      const quoterOverlay = document.getElementById("quoter--overlay");
      if (quoterOverlay) {
        quoterOverlay.remove();
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <div className="home-app-wrapper">
      <LayOutContainer>
        {/* Cargar Hero y BannerPromo directamente sin Suspense */}
        <Hero setOpenMod={setOpenMod} openMod={openMod} />
        <Ticker />
        <BannerPromo setOpenMod={setOpenMod} scrollToAnchor={scrollToAnchor} />
        <Suspense fallback={<Feature1Skeleton />}>
          <Feature1 />
        </Suspense>
        <Suspense fallback={<Feature2Skeleton />}>
          <Feature2 setOpenMod={setOpenMod} />
        </Suspense>
        <Suspense fallback={<Feature3Skeleton />}>
          <Feature3 setOpenMod={setOpenMod} />
        </Suspense>
        <Suspense fallback={<Feature4Skeleton />}>
          <Feature4 setOpenMod={setOpenMod} />
        </Suspense>
        <Suspense fallback={<Feature5Skeleton />}>
          <Feature5 setOpenMod={setOpenMod} />
        </Suspense>
        <Suspense fallback={<BannerFullXSkeleton />}>
          <BannerFullX />
        </Suspense>
        <Suspense fallback={<BannerPaymentMethodsSkeleton />}>
          <BannerPaymentMethods setOpenMod={setOpenMod} />
        </Suspense>
        <Suspense fallback={<FaqSkeleton />}>
          <Faq />
        </Suspense>
        <Suspense fallback={<LandingSliderSkeleton />}>
          {sliderComponents[currentCountry]}
        </Suspense>
        <Suspense fallback={<NewsletterSkeleton />}>
          <Newsletter />
        </Suspense>
        {geo === "ar" && (
          <Suspense fallback={<BlogFeedsSkeleton />}>
            <BlogFeeds />
          </Suspense>
        )}
        <Suspense fallback={<BannerPreFooterSkeleton />}>
          <BannerPreFooter setOpenMod={setOpenMod} />
        </Suspense>

        {/* Coverages component from shared-ui */}
        <div id="coverages-container" ref={coveragesRef}></div>
      </LayOutContainer>
    </div>
  );
};

export default Home;
